import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";

// @material-ui/icons
import Warning from "@material-ui/icons/Warning";
import Check from "@material-ui/icons/Check";

// @material-ui components
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";

// core components
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";

// services
import { listarPendentes, confirmarLeitura } from "beaktMatriz/views/Avisos/AvisoIndiceVigenteService.js";
import { formateDate, transformValueFloattoFormated } from "beaktShared/helpers/masks";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

const useStyles = makeStyles({
  ...styles,
  aviso: {
    borderTop: "1px solid #e0e0e0",
    padding: "10px 0",
  },
  info: {
    color: "#777",
    fontSize: "13px",
    "& > span": {
      marginRight: "16px"
    }
  },
});

// Os avisos nascem no job de reajuste (sem ação na tela); consulta ao carregar e nas trocas de rota, no máximo 1x/min
const INTERVALO_MINIMO_CONSULTA_MS = 60 * 1000;

// BKT-1509: popup de leitura obrigatória (perfil Matriz) do índice vigente aplicado por fallback.
// Sem fechar, ESC ou clique fora: só some após a confirmação. Em falha de consulta não exibe (fail-open),
// o aviso continua pendente na API e reaparece na próxima consulta.
export default function AvisoIndiceVigente() {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();

  const [avisos, setAvisos] = useState([]);
  const [ciente, setCiente] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [erroConfirmacao, setErroConfirmacao] = useState("");

  const ultimaConsultaRef = useRef(0);
  const consultandoRef = useRef(false);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  const consultar = useCallback((forcar) => {
    if (consultandoRef.current)
      return Promise.resolve();
    if (!forcar && Date.now() - ultimaConsultaRef.current < INTERVALO_MINIMO_CONSULTA_MS)
      return Promise.resolve();

    consultandoRef.current = true;
    ultimaConsultaRef.current = Date.now();
    return listarPendentes().then(result => {
      consultandoRef.current = false;
      if (!montadoRef.current)
        return;
      if (!result.ok)
        ultimaConsultaRef.current = 0;
      setAvisos(result.data);
    });
  }, []);

  const aberto = avisos.length > 0;
  const primeiraConsultaRef = useRef(true);
  useEffect(() => {
    if (aberto)
      return;
    consultar(primeiraConsultaRef.current);
    primeiraConsultaRef.current = false;
  }, [location.pathname, aberto, consultar]);

  const handleConfirmar = () => {
    if (!ciente || confirmando)
      return;

    setConfirmando(true);
    setErroConfirmacao("");
    confirmarLeitura(avisos.map(a => a.avisoIndiceVigenteId)).then(result => {
      if (!montadoRef.current)
        return;
      if (!result.ok) {
        setConfirmando(false);
        setErroConfirmacao(result.message || t('current_index_notice.confirm_error'));
        return;
      }
      // Confirma na API antes de fechar: pode ter surgido aviso novo enquanto o popup estava aberto
      consultar(true).then(() => {
        if (!montadoRef.current)
          return;
        setConfirmando(false);
        setCiente(false);
      });
    });
  };

  if (!aberto)
    return null;

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
      aria-labelledby="aviso-indice-vigente-titulo"
    >
      <DialogContent>
        <Card>
          <CardHeader color="warning" icon>
            <CardIcon color="warning">
              <Warning />
            </CardIcon>
            <h4 className={classes.cardIconTitle} id="aviso-indice-vigente-titulo">
              {t('current_index_notice.title')}
            </h4>
          </CardHeader>
          <CardBody>
            <p>{t('current_index_notice.description')}</p>

            {avisos.map(aviso => (
              <div key={aviso.avisoIndiceVigenteId} className={classes.aviso}>
                <b>{aviso.estabelecimento}</b>
                <div>{aviso.mensagem}</div>
                <div className={classes.info}>
                  <span>{t('current_index_notice.reference')}: {aviso.mesAnoReferencia}</span>
                  <span>{t('current_index_notice.index_applied')}: {aviso.indiceAplicado}</span>
                  <span>{t('current_index_notice.percentage')}: {transformValueFloattoFormated(aviso.percentual || 0)}%</span>
                  <span>{t('label.date')}: {formateDate(aviso.dataCriacao)}</span>
                </div>
              </div>
            ))}

            {erroConfirmacao &&
              <SnackbarContent color="danger" message={erroConfirmacao} />}

            <FormControlLabel
              control={
                <Checkbox
                  tabIndex={-1}
                  checked={ciente}
                  disabled={confirmando}
                  onChange={e => setCiente(e.target.checked)}
                  checkedIcon={<Check className={classes.checkedIcon} />}
                  icon={<Check className={classes.uncheckedIcon} />}
                  classes={{ checked: classes.checked, root: classes.checkRoot }}
                />
              }
              classes={{ label: classes.label, root: classes.labelRoot }}
              label={t('current_index_notice.aware_checkbox')}
            />
            <div className={classes.textCenter}>
              <Button color="primary" disabled={!ciente || confirmando} onClick={handleConfirmar}>
                {confirmando
                  ? <><CircularProgress size={16} color="inherit" /> {t('label.loading')}...</>
                  : t('current_index_notice.confirm_reading')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
